import React from 'react';
import { MapPin, MessageCircle, Gem, CheckCircle, Crown } from 'lucide-react';
import { Escort } from '../types';
import { getWhatsAppUrl } from "../utils/whatsappUtils.ts";
import { capitalizeFirstLetter } from '../utils/textUtils';
import { useTranslation } from '../hooks/useTranslation';
import { useDisclaimer } from '../contexts/DisclaimerContext';
import useMobile from '../hooks/useMobile';

interface CardProps {
  escort: Escort;
  onFavorite?: (id: string) => void;
  onViewProfile: (id: string) => void;
  priority?: boolean; // Per ottimizzare LCP della prima card
}

const Card: React.FC<CardProps> = ({ escort, onFavorite, onViewProfile, priority = false }) => {
  const isMobile = useMobile();
  const { t } = useTranslation();
  const { hasAcceptedDisclaimer } = useDisclaimer();

  const handleWhatsAppClick = () => {
    if (escort.whatsappNumber) {
      window.open(getWhatsAppUrl(escort.whatsappNumber), '_blank');
    }
  };

  // Estrai la città dal campo location (formato: "Città / Provincia")
  const getCity = (location: string) => {
    return location.split(' / ')[0];
  };

  return (
    <div
      className={`rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 bg-white relative cursor-pointer group ${escort.featured ? 'border-2 border-amber-400' : escort.isVetrina ? 'border-2 border-yellow-300' : ''}`}
      onClick={() => onViewProfile(escort.id)}
      style={{ 
        minHeight: '480px',
        height: '480px',
        contain: 'layout'
      }}
    >
      <div className="relative" style={{ height: '320px', overflow: 'hidden', contain: 'layout' }}>
        {hasAcceptedDisclaimer ? (
          <img 
            src={escort.imageUrl} 
            alt={escort.name} 
            className="w-full h-full object-cover"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            width="320"
            height="320"
            style={{ aspectRatio: '1 / 1', display: 'block' }}
          />
        ) : (
          <div 
            className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center"
            style={{ aspectRatio: '1 / 1' }}
          >
            <div className="text-gray-400 text-center px-4">
              <div className="text-4xl mb-2">🔒</div>
              <div className="text-sm font-medium">{t('card.imageBlocked')}</div>
            </div>
          </div>
        )}
        
        {/* Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div 
            className="text-white/20 text-2xl font-bold transform rotate-12 select-none"
            style={{ minWidth: '200px', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {t('common.escortfun_net')}
          </div>
        </div>
        
        {/* WhatsApp Icon */}
        {escort.whatsappNumber && (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleWhatsAppClick();
            }}
            className="absolute top-3 left-3 bg-green-500 p-2 rounded-full shadow-lg hover:bg-green-600 transition-colors duration-200 z-10"
            aria-label="Contatta su WhatsApp"
            style={{ minWidth: '24px', minHeight: '24px' }}
          >
            <MessageCircle className="h-5 w-5 text-white" />
          </button>
        )}

        {/* VIP Badge */}
        {escort.featured && (
          <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-400 to-yellow-600 text-white text-xs px-3 py-1 rounded-full flex items-center z-10 shadow-lg border border-amber-300" style={{ minWidth: '60px', minHeight: '24px' }}>
            <Crown className="h-3 w-3 mr-1" />
            {t('common.vipBadge')}
          </div>
        )}
        
        {/* Vetrina Badge */}
        {!escort.featured && escort.isVetrina && (
          <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs px-3 py-1 rounded-full flex items-center z-10 shadow-lg border border-yellow-300" style={{ minWidth: '80px', minHeight: '24px' }}>
            <Gem className="h-3 w-3 mr-1" />
            {t('card.showcaseBadge')}
          </div>
        )}
        
        {/* Verified Badge */}
        {escort.isVerified && (
          <div className="absolute bottom-3 left-3 bg-green-500 text-white text-xs px-3 py-1 rounded-full flex items-center z-10 shadow-lg" style={{ minWidth: '100px', minHeight: '24px' }}>
            <CheckCircle className="h-3 w-3 mr-1" />
            {t('common.verifiedBadge')}
          </div>
        )}
      </div>


      {isMobile ? (
        <div className="flex items-center justify-between px-2 py-2 md:p-4" style={{ minHeight: '160px', height: '160px', overflow: 'hidden' }}>
          
          <div className="flex items-center min-w-0 flex-1">
            <span className="text-base font-semibold text-gray-800 truncate mr-2" style={{ minWidth: '80px' }}>{escort.name}</span>
            <div className="flex items-center text-gray-600" style={{ marginTop: '1px', minWidth: '100px' }}>
              <MapPin className="h-4 w-4 mr-1 text-pink-500 flex-shrink-0" />
              <span className="text-sm truncate">{getCity(escort.location)}</span>
            </div>
          </div>

          {escort.category && (
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-xs px-3 py-1 rounded-full border border-purple-200 ml-2 whitespace-nowrap flex-shrink-0 text-center" style={{ minWidth: '80px', minHeight: '24px' }}>
              {capitalizeFirstLetter(escort.category)}
            </div>
          )}
        </div>
      ) : (
        <div className="p-4" style={{ minHeight: '160px', height: '160px', overflow: 'hidden' }}>
          <h3 className="text-lg font-semibold text-gray-800 mb-1" style={{ minHeight: '28px' }}>
            {escort.name}
          </h3>
          <div className="flex items-center justify-between mb-2" style={{ minHeight: '32px' }}>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-1 text-pink-500" />
              <span className="text-sm">{escort.location}</span>
            </div>
            {escort.category && (
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-xs px-3 py-1 rounded-full border border-purple-200 text-center" style={{ minWidth: '80px', minHeight: '24px' }}>
                {capitalizeFirstLetter(escort.category)}
              </div>
            )}
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Card;